import { useState } from "react";
import { Play } from "lucide-react";

/**
 * Lite YouTube embed — renders the thumbnail with a play overlay; only
 * mounts the YouTube iframe (and its ~500KB player bundle) on click.
 *
 * Detail pages get switched between rapidly while reading digests; an
 * always-on iframe per page would load the player bundle on every nav
 * and inflate analytics noise. This pattern follows lite-youtube-embed
 * (paulirish) — hand-rolled here to avoid a dep.
 */
export function VideoEmbed({ videoId }: { videoId: string }) {
  const [playing, setPlaying] = useState(false);
  const [thumbSrc, setThumbSrc] = useState(
    `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
  );

  return (
    <div className="mb-6 max-w-[640px]">
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black">
        {playing ? (
          <iframe
            title="YouTube video player"
            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`}
            className="absolute inset-0 h-full w-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 h-full w-full cursor-pointer"
            aria-label="Play video"
          >
            <img
              src={thumbSrc}
              alt=""
              loading="lazy"
              onError={() =>
                setThumbSrc(
                  `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
                )
              }
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/15">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-indigo text-white shadow-lg transition-transform group-hover:scale-105">
                <Play size={28} className="ml-1 fill-current" />
              </div>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}
